import { MapPin, Clock, CreditCard, Phone, Wifi, Instagram, CalendarDays, Navigation, QrCode, Truck, Info, Link2 } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import type { Store } from '../../store/useStore'

interface InfoTabProps {
  store: Store
  // #314 task #2: when self-order requires a table-QR scan and the user
  // hasn't done one, surface "how to order here" inside the Info tab
  // rather than as a top banner. Browsing customers (looking up the menu
  // before they walk in) shouldn't be harassed by a notice they don't
  // need yet.
  tableRequiredNotice?: string
}

// #2136 お店のレジで使える支払い。**客に見せる順**で並べる (サーバも同じ順に正規化する)
const STORE_PAYMENT_LABELS = [
  ['cash', 'storeDetail.payCash'],
  ['credit_card', 'storeDetail.payCreditCard'],
  ['emoney', 'storeDetail.payEmoney'],
  ['qr_code', 'storeDetail.payQr'],
] as const

export default function InfoTab({ store, tableRequiredNotice }: InfoTabProps) {
  const { t, locale } = useI18n()
  // #2136 **アプリで払えるか**はシステムが知っている事実 (KYC = chargesEnabled)。
  // menu-only の店は注文も会計も店舗側の仕組みなので、アプリでは払えない。
  const menuOnly = store.orderingMode === 'menu-only'
  const payInApp = !menuOnly && store.paymentEnabled !== false
  // **お店のレジで何が使えるかは店しか知らない。** 設定されたものだけ出す
  const storePayments = (store.storePaymentMethods || []).filter((m) =>
    STORE_PAYMENT_LABELS.some(([key]) => key === m))

  return (
    <div className="px-4 pt-4 pb-8 space-y-3">
      {/* #329 キッチンカー: 今日の出店場所 (スケジュール運用店のみ。backend の
          decoration が解決済み。null = 本日出店なし)。経路リンクは今日の座標へ */}
      {store.kitchenCarToday !== undefined && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Truck size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-body text-xs text-text-tertiary">{t('storeDetail.kcTodayPlace')}</p>
              {store.kitchenCarToday ? (
                <>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="font-body text-sm font-medium text-text-primary truncate">
                      {store.kitchenCarToday.placeName}
                    </p>
                    {store.kitchenCarToday.isOpenNow && (
                      <span className="shrink-0 rounded-full bg-matcha/15 px-1.5 py-0.5 text-[10px] font-bold font-body text-matcha">
                        {t('stores.kcOpenNow')}
                      </span>
                    )}
                  </div>
                  <p className="font-body text-xs text-text-secondary mt-0.5">
                    {store.kitchenCarToday.openTime} - {store.kitchenCarToday.closeTime}
                  </p>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${store.kitchenCarToday.latitude},${store.kitchenCarToday.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-yokoso-red font-body text-xs font-medium tap-effect mt-1.5"
                  >
                    <Navigation size={12} />
                    {t('storeDetail.getDirections')}
                  </a>
                </>
              ) : (
                <p className="font-body text-sm text-text-primary mt-0.5">
                  {t('stores.kcClosedToday')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-4">
        <div className="flex items-start gap-3">
          <MapPin size={18} className="text-text-tertiary mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-body text-xs text-text-tertiary">{t('storeDetail.addressLabel')}</p>
            {/* #696: building (建物名・部屋番号) があれば address の後ろに空白区切りで append */}
            <p className="font-body text-sm text-text-primary mt-0.5">{[store.address, store.building].filter(Boolean).join(' ')}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
              {/* #721: googleMapsUrl が設定されてれば実店舗 listing に直リンク
                  (= レビュー / 写真が見える)。未設定なら住所 search に fallback。
                  「経路案内」(下) は destination=住所テキストの方が地図 app の
                  ルート探索 UX が安定するので googleMapsUrl とは独立した実装のまま。 */}
              <a
                href={store.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([store.address, store.building].filter(Boolean).join(' '))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-yokoso-red font-body text-xs font-medium tap-effect"
              >
                <MapPin size={12} />
                {t('storeDetail.openMap')}
              </a>
              {/* #721: googleMapsUrl が set されている店舗は「店名 + 住所」で
                  fuzzy 検索 → Google Maps の Place listing にヒットしやすく、
                  店舗名表示の経路ガイドになる (yokoso-pay は個店向けで
                  チェーン展開なし、= 同名衝突の懸念実質ゼロ)。
                  googleMapsUrl 未設定の店舗は従来通り住所単独で検索 (= オーナー
                  が Google 登録を主張していない時に、店名 fuzzy 検索が誤マッチ
                  する余地を作らない conservative fallback)。 */}
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  store.googleMapsUrl
                    ? `${store.name} ${[store.address, store.building].filter(Boolean).join(' ')}`
                    : [store.address, store.building].filter(Boolean).join(' ')
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-yokoso-red font-body text-xs font-medium tap-effect"
              >
                <Navigation size={12} />
                {t('storeDetail.getDirections')}
              </a>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Clock size={18} className="text-text-tertiary mt-0.5 shrink-0" />
          <div>
            <p className="font-body text-xs text-text-tertiary">{t('storeDetail.hoursLabel')}</p>
            <p className="font-body text-sm text-text-primary mt-0.5">{store.hours}</p>
            {/* #2134 定休日は**店が設定したときだけ**出す。以前はここに
              * 「定休日: 不定休」が固定で入っていて、木曜定休の店でも「不定休」と
              * 言い切っていた。分からないことを既定値で埋め直さない。 */}
            {store.closedDays && (
              <p className="font-body text-xs text-text-tertiary mt-0.5">
                {t('storeDetail.closedDaysLabel')}: {store.closedDays}
              </p>
            )}
          </div>
        </div>
        {/* #1043: 実データがある店舗のみ表示（旧プレースホルダー tel:0463-XX-XXXX を廃止） */}
        {store.phoneNumber && (
          <div className="flex items-start gap-3">
            <Phone size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div>
              <p className="font-body text-xs text-text-tertiary">{t('storeDetail.phoneLabel')}</p>
              <a href={`tel:${store.phoneNumber}`} className="font-body text-sm text-text-primary mt-0.5 block">
                {store.phoneNumber}
              </a>
            </div>
          </div>
        )}
      </div>

      {/* SNS — 実際にリンクを描画するのはハンドル2種だけなので、表示条件も
        * それに合わせる。以前の「snsMedia のどれかに値があれば表示」だと、
        * 公式投稿URL・ハッシュタグ (#69) だけ設定した店で空のブロックが出る。 */}
      {(store.snsMedia?.instagramHandle || store.snsMedia?.twitterHandle) && (
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="font-body text-xs text-text-tertiary mb-3">SNS</p>
          <div className="space-y-3">
            {store.snsMedia.instagramHandle && (
              <a
                href={`https://instagram.com/${store.snsMedia.instagramHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm font-body text-text-primary hover:text-yokoso-red transition-colors"
              >
                <Instagram size={20} className="text-pink-500" />
                <span>@{store.snsMedia.instagramHandle}</span>
              </a>
            )}
            {store.snsMedia.twitterHandle && (
              <a
                href={`https://x.com/${store.snsMedia.twitterHandle?.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm font-body text-text-primary hover:text-yokoso-red transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center font-bold text-base">𝕏</span>
                <span>{store.snsMedia.twitterHandle}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Reservation Info */}
      {store.reservationEnabled && store.reservationConfig && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CalendarDays size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-body text-xs text-text-tertiary">{t('storeDetail.reservationInfoLabel')}</p>
              <p className="font-body text-sm text-text-primary mt-0.5">
                {store.reservationConfig.timeRange.start} - {store.reservationConfig.timeRange.end}
                <span className="mx-1.5 text-text-tertiary">·</span>
                {t('storeDetail.reservationPartyRangeShort', {
                  min: String(store.reservationConfig.partySizeRange.min),
                  max: String(store.reservationConfig.partySizeRange.max),
                })}
              </p>
              <div className="flex flex-wrap gap-1 mt-2">
                {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                  const active = store.reservationConfig!.availableDays.includes(d)
                  return (
                    <span
                      key={d}
                      className={`rounded px-1.5 py-0.5 text-[10px] font-body font-medium ${
                        active ? 'bg-matcha/20 text-matcha' : 'bg-gray-200 text-text-tertiary line-through'
                      }`}
                    >
                      {t(`storeSettings.reservationDayShort.${d}`)}
                    </span>
                  )
                })}
              </div>
              <p className="font-body text-xs text-text-tertiary mt-2 leading-relaxed">
                {t('storeDetail.reservationInfoHint', {
                  duration: String(store.reservationConfig.durationMinutes),
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Transaction group — 決済方法 + 注文方法 together (they both answer
       * "how do I transact at this store"). Wi-Fi is an amenity, not a
       * transaction method, so it gets its own card below. */}
      <div className="bg-gray-50 rounded-xl p-4 space-y-4">
        {/* #2136 **知っていることだけ言う。** 以前はここに YOKOSO PAY / Credit Card /
          * Cash が**全店ハードコード**で並んでいて、アプリで払えない店 (#2115 の
          * menu-only) まで「カードが使える」と名乗っていた。
          *   アプリで払えるか … システムが知っている (KYC)
          *   店のレジで何が使えるか … 店しか知らない。設定されたものだけ出す */}
        {(payInApp || storePayments.length > 0 || menuOnly) && (
          <div className="flex items-start gap-3">
            <CreditCard size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="font-body text-xs text-text-tertiary">{t('storeDetail.paymentLabel')}</p>
              {payInApp && (
                <div>
                  <p className="font-body text-[11px] text-text-tertiary">{t('storeDetail.payInApp')}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <span className="rounded-full bg-navy/10 px-2.5 py-0.5 text-xs font-medium text-navy">YOKOSO PAY</span>
                    <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-600">Credit Card</span>
                  </div>
                </div>
              )}
              {menuOnly && (
                <p className="font-body text-sm text-text-primary leading-relaxed">
                  {t('storeDetail.payAtStore')}
                </p>
              )}
              {storePayments.length > 0 && (
                <div>
                  <p className="font-body text-[11px] text-text-tertiary">{t('storeDetail.payAtStoreLabel')}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {STORE_PAYMENT_LABELS.filter(([key]) => storePayments.includes(key)).map(([key, labelKey]) => (
                      <span key={key} className="rounded-full bg-matcha/10 px-2.5 py-0.5 text-xs font-medium text-matcha">
                        {t(labelKey)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {tableRequiredNotice && (
          <div className="flex items-start gap-3">
            <QrCode size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-body text-xs text-text-tertiary">
                {t('storeDetail.howToOrderLabel')}
              </p>
              <p className="font-body text-sm text-text-primary mt-0.5 leading-relaxed">
                {tableRequiredNotice}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* #2136 公式サイトとその他のリンク。**店が入れたものだけ**。
        * 代行掲載の店 (#2121) は公式サイトしか情報源が無いことが多く、客が
        * 「もっと見たい」と思ったときの行き先がアプリの外にある。 */}
      {(store.websiteUrl || (store.otherLinks || []).length > 0) && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Link2 size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-body text-xs text-text-tertiary">{t('storeDetail.linksLabel')}</p>
              <div className="mt-1 space-y-1.5">
                {store.websiteUrl && (
                  <a
                    href={store.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block font-body text-sm text-yokoso-red font-medium tap-effect truncate"
                  >
                    {t('storeDetail.officialSite')}
                  </a>
                )}
                {(store.otherLinks || []).map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block font-body text-sm text-yokoso-red font-medium tap-effect truncate"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* #2136 Wi-Fi。**店が設定したときだけ出す。** 以前は全店に「Free Wi-Fi あり」と
        * 固定で出ていた (定休日が全店「不定休」だったのと同じ #2134)。 */}
      {store.wifiAvailable !== undefined && store.wifiAvailable !== null && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Wifi size={18} className="text-text-tertiary mt-0.5 shrink-0" />
            <div>
              <p className="font-body text-xs text-text-tertiary">Wi-Fi</p>
              <p className="font-body text-sm text-text-primary mt-0.5">
                {t(store.wifiAvailable ? 'storeDetail.wifiAvailable' : 'storeDetail.wifiNone')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* #2121 代行掲載。**弊社が店の許諾をもらって載せている店**（#2117）だけに出す。
          出典を隠すと、価格が古かったときに「誰が出した情報か」が客に分からない。
          店にアカウントを渡すと listingManagedBy が 'store' に戻り、自動で消える。
          警告ではなく出典なので、赤帯ではなく小さい注記として一番下に置く。 */}
      {store.listingManagedBy === 'agency' && (
        <div className="flex items-start gap-2 px-1 pt-1">
          <Info size={14} className="text-text-tertiary mt-0.5 shrink-0" />
          <p className="font-body text-xs text-text-tertiary leading-relaxed">
            {t('storeDetail.agencyListingNote')}
            {monthLabel(store.listingCheckedAt, locale) && (
              <>{' '}{t('storeDetail.agencyListingChecked', { date: monthLabel(store.listingCheckedAt, locale) })}</>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * `YYYY-MM-DD` を「2026年10月」のように年月で出す。
 *
 * **日まで出さない。** ここの日付は弊社が価格を確かめた日で、客にとって意味が
 * あるのは「どのくらい前か」まで。日まで出すと、翌日には古いように見える。
 * 読めない値 (未設定・壊れた文字列) は空文字を返し、呼ぶ側が一文ごと出さない。
 */
function monthLabel(value: string | undefined, locale: string): string {
  if (!value) return ''
  const d = new Date(`${value}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long' }).format(d)
  } catch {
    return value.slice(0, 7)
  }
}
